import { Entity, World } from '$legacyecstatic';
import { Matrix4, Vector3 } from '@math.gl/core';
import { Components } from 'editor/components';
import MapboxRasterSource from 'editor/components/map/MapboxRasterSource';
import MapSheet from 'editor/components/map/MapSheet';
import Markup from 'editor/components/Markup';
import MarkupKit from 'editor/components/MarkupKit';
import { LngLat, MercatorCoordinate } from 'mapbox-gl';
import { ImageAlignment } from 'records/MappedSheetVersion';
import { pointInPolygon } from 'utils/hitDetection';

// Compute the matrix used for hit detection in sheet space.
// Used to go from sheet space to map space (lnglat);
// Can invert the resulting matrix for use in hit dectection.
export function createSheetMatrix(
  coords: ImageAlignment,
  width: number,
  height: number
): Matrix4 {
  const a =
    (coords.bottomright.longitude - coords.bottomleft.longitude) / width;
  const b = (coords.bottomright.latitude - coords.bottomleft.latitude) / width;
  const c = (coords.topleft.longitude - coords.bottomleft.longitude) / height;
  const d = (coords.topleft.latitude - coords.bottomleft.latitude) / height;
  const tx = coords.bottomleft.longitude;
  const ty = coords.bottomleft.latitude;

  // prettier-ignore
  const mat = new Matrix4([
    a,  b,  0,  0,
    c,  d,  0,  0,
    0,  0,  1,  0, 
    tx, ty, 0,  1
  ]);

  return mat;
}

// Creates a matrix that can be used to go from sheet coordinates to mapboxgl Mercator coordinates.
// NOTE: Mapbox's way of doing Mercator is slightly different than normal Mercator. Namely { 0, 0 }
//       is in the NW corner, and { 1, 1 } is SE corner. See the MapboxGL docs for more info:
//       https://docs.mapbox.com/mapbox-gl-js/api/geography/#mercatorcoordinate
export function createMercatorMatrix(
  coords: ImageAlignment,
  width: number,
  height: number,
  worldSize: number
): Matrix4 {
  const { x: tlx, y: tly } = MercatorCoordinate.fromLngLat([
    coords.topleft.longitude,
    coords.topleft.latitude,
  ]);

  const { x: brx, y: bry } = MercatorCoordinate.fromLngLat([
    coords.bottomright.longitude,
    coords.bottomright.latitude,
  ]);

  const { x: blx, y: bly } = MercatorCoordinate.fromLngLat([
    coords.bottomleft.longitude,
    coords.bottomleft.latitude,
  ]);

  const a = (brx - blx) / width;
  const b = (bry - bly) / width;
  const c = (tlx - blx) / height;
  const d = (tly - bly) / height;
  const tx = blx;
  const ty = bly;

  // prettier-ignore
  const mat = new Matrix4([
  a * worldSize,  b * worldSize,  0,  0,
  c * worldSize,  d * worldSize,  0,  0,
  0,              0,              1,  0, 
  tx * worldSize, ty * worldSize, 0,  1
]);

  return mat;
}

// NOTE: Maybe ImageAlignment should be a construct and not just a type?
//       Might be nice to have conversion methods on it.
export function sourceCoordsToImageAlignment(c: number[][]): ImageAlignment {
  return {
    topleft: { longitude: c[0][0], latitude: c[0][1] },
    topright: { longitude: c[1][0], latitude: c[1][1] },
    bottomright: { longitude: c[2][0], latitude: c[2][1] },
    bottomleft: { longitude: c[3][0], latitude: c[3][1] },
  };
}

export function getHitOnSheet(
  c: number[][],
  width: number,
  height: number,
  mapHit: Vector3
): Vector3 | null {
  // Checking if in sheet bounds for now, but might be good to not enforce this check later
  // so that overflow markups can work.
  if (pointInPolygon(mapHit.x, mapHit.y, c)) {
    const coords = sourceCoordsToImageAlignment(c);
    const sheetTransform = createSheetMatrix(coords, width, height).invert(); // Inverted because we are going from sheet to lnglat.
    const layerHit = mapHit.transform(sheetTransform);
    return layerHit;
  }

  return null;
}

export function getSheetHitMarkups(
  world: World<Components>,
  c: number[][],
  width: number,
  height: number,
  mapHit: Vector3
): Entity<Components>[] {
  const layerHit = getHitOnSheet(c, width, height, mapHit);
  if (!layerHit) return [];

  return world.locateAll([Markup, MarkupKit]).filter((e) => {
    const m = e.get(Markup);
    const mk = e.get(MarkupKit);
    const point = new mk.MKPoint(layerHit.x, layerHit.y);

    if (!m.mkMarkup || m.hidden) {
      return false;
    }

    return m.mkMarkup.hit(point);
  });
}

export interface LayerHit {
  entity: Entity<Components>;
  hit: Vector3 | null;
  x: number;
  y: number;
}

// TODO: This should be updated to not take an entity, but map coordinate rect.
export function getSheetLayerHit(
  entity: Entity<Components>,
  x: number,
  y: number
): LayerHit {
  const sourceComp = entity.get(MapboxRasterSource);

  const { coordinates, width, height } = sourceComp.tracked;

  if (!coordinates) return { entity, hit: null, x, y };

  const mapHit = new Vector3(x, y, 1);
  const hit = getHitOnSheet(coordinates, width, height, mapHit);

  return { entity, hit, x, y };
}

export function getSheetLayerHits(
  world: World<Components>,
  x: number,
  y: number
): LayerHit[] {
  return world.locateAll(MapSheet).map((ent) => getSheetLayerHit(ent, x, y));
}

export function sheetPointToLngLat(
  coords: ImageAlignment,
  sheetWidth: number,
  sheetHeight: number,
  sheetPoint: Point
): LngLat {
  const sheetMat = createSheetMatrix(coords, sheetWidth, sheetHeight);

  const sheetHit = new Vector3(sheetPoint.x, sheetPoint.y, 1);

  const r = sheetHit.transform(sheetMat);

  return new LngLat(r.x, r.y); // these might be reversed, I can never remember.
}
